import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { ArrowPathIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useLayoutEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BeatLoader } from "react-spinners";
import * as Yup from "yup";
import { theme } from "../../../utils/theme";
import { PlayButton } from "../../Buttons/PlayButton";
import { VoiceClonerData } from "../VoiceClonerModal";
import {
  fetchPresetVoices as fetchPresetVoicesApi,
  PresetVoice,
} from "../../../api/voices";

type Props = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  onNext: (data: Partial<VoiceClonerData>) => void;
};

export const PresetVoiceSelector: FC<Props> = ({ open, setOpen, onNext }) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const size = 6;
  const [presetVoices, setPresetVoices] = useState<PresetVoice[]>([]);
  const hasMore = index + size < presetVoices.length;

  type PresetVoiceFormData = {
    voiceType: "Preset";
    presetVoiceId: string;
    presetVoiceName: string;
  };

  const PresetVoiceFormSchema = Yup.object().shape({
    voiceType: Yup.string()
      .oneOf(["Preset"], t("errors.required"))
      .required(t("errors.required")),
    presetVoiceId: Yup.string().required(t("errors.required")),
    presetVoiceName: Yup.string().required(t("errors.required")),
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PresetVoiceFormData>({
    defaultValues: {
      voiceType: "Preset",
      presetVoiceId: undefined,
      presetVoiceName: "",
    },
    resolver: yupResolver(PresetVoiceFormSchema),
  });

  const onSubmit = (data: PresetVoiceFormData) => {
    onNext(data);
    reset();
  };

  const selectedPresetVoiceId = watch("presetVoiceId");

  const fetchPresetVoices = async () => {
    setPresetVoices(await fetchPresetVoicesApi());
  };

  useLayoutEffect(() => {
    fetchPresetVoices();
  }, [presetVoices.length]);

  const getMore = () => {
    reset();
    const newIndex = hasMore ? index + size : 0;
    setIndex(newIndex);
  };

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-slate-800 px-4 pb-4 pt-5 text-left w-full max-w-sm sm:w-full sm:max-w-xl shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <form
              className="bg-white flex flex-1 flex-col justify-between"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded bg-white text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon aria-hidden="true" className="size-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <DialogTitle
                    as="h3"
                    className="text-2xl font-semibold text-slate-800"
                  >
                    {t("audioBook.title")}
                  </DialogTitle>
                  <hr className="mt-2 w-full border-gray-200" />
                </div>
              </div>
              <div className="flex flex-col mt-5 justify-around">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 space-y-2 sm:space-y-0">
                  <p className="text-sm sm:text-md">
                    {t("audioBook.chooseVoice")}
                  </p>

                  <div className="flex items-center space-x-2">
                    <p className="text-xs sm:text-sm text-gray-500 mr-2">
                      {index + size < presetVoices.length
                        ? index + size
                        : presetVoices.length}{" "}
                      / {presetVoices.length}
                    </p>
                    <button
                      type="button"
                      className="p-1.5 sm:p-2 bg-rust-500 hover:bg-rust-100 rounded"
                      onClick={getMore}
                    >
                      <ArrowPathIcon className="w-3 h-3 sm:w-4 sm:h-4 text-white transition-colors" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  {presetVoices.length > 0 ? (
                    <>
                      {presetVoices
                        .slice(index, index + size)
                        .map((presetVoice) => {
                          return (
                            <div
                              key={presetVoice.voice_id}
                              onClick={() => {
                                setValue("presetVoiceId", presetVoice.voice_id);
                                if (presetVoice?.name) {
                                  setValue(
                                    "presetVoiceName",
                                    presetVoice?.name?.split(" ")[0]
                                  );
                                }
                              }}
                              className={[
                                selectedPresetVoiceId === presetVoice.voice_id
                                  ? "border-herb-700"
                                  : "border-gray-200",
                                "flex flex-col sm:flex-row items-center px-2 sm:px-4 py-2 border-2 rounded h-fit cursor-pointer",
                              ].join(" ")}
                            >
                              <div className="mb-2 sm:mb-0 sm:mr-2">
                                <PlayButton
                                  audioUrl={presetVoice.preview_url || ""}
                                />
                              </div>
                              <p className="text-center sm:text-left text-xs sm:text-sm flex-1">
                                {presetVoice.name?.split(" ")[0]}
                              </p>
                            </div>
                          );
                        })}
                    </>
                  ) : (
                    <div className="items-center justify-center col-span-2 sm:col-span-3 flex h-24 sm:h-32">
                      <BeatLoader color={theme.colors.delicate[200]} />
                    </div>
                  )}
                </div>
                {errors && errors.presetVoiceId && (
                  <p className="text-red-500 text-xs sm:text-sm mt-2">
                    {t("audioBook.selectVoiceError")}
                  </p>
                )}
              </div>

              <hr className="mt-6 sm:mt-8 w-full border-gray-200" />
              <div className="mt-2 flex justify-center sm:mx-auto">
                <button
                  type="submit"
                  className="w-full sm:w-auto inline-flex justify-center rounded bg-rust-50 px-4 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-rust-300 hover:bg-rust-100"
                >
                  {t("next")}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
